import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  Grid,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
} from "@mui/material";
import { Formik, Form, FieldArray, FormikProps } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MpesaIcon from "@mui/icons-material/PhoneAndroid";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import AppModal from "../common/AppModal";
import FormAlert from "../common/FormAlert";
import FormSearchSelect from "../common/FormSearchSelect";
import FormSelectField from "../common/FormSelectField";
import FormTextField from "../common/FormTextField";

import StkPushModal from "../payments/StkPushModal";
import AllocatePaymentModal from "../payments/AllocatePaymentModal";
import AppButton from "../buttons/AppButton";

import { createReceipt } from "../../redux/actions/receiptActions";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { receiptActions } from "../../redux/slices/receipt";
import { ReceiptRequestDto } from "../../shared/dtos/receipt/ReceiptRequestDto";
import { PaymentMethodType } from "../../shared/enums/PaymentMethodType";

interface PreviewPaymentField {
  paymentMethodId: string;
  amount: number | "";
  unallocatedPaymentId: string | null;
  referenceCode: string | null;
}

interface PreviewFormValues {
  receiptType: "CASH" | "CREDIT";
  customerId: string;
  currencyCode: string;
  exchangeRate: number | string;
  chargeIds: string[];
  payments: PreviewPaymentField[];
}

const ReceiptPreviewModal: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  // --- REDUX STATE ---
  const {
    cartItems: items,
    cartSubtotal: subtotal,
    saleType,
    loadingCreate: loadingCreateReceipt,
    errorCreate: errorCreateReceipt,
  } = useAppSelector((state) => state.receipt);

  const { currentShift } = useAppSelector((state) => state.shift);
  const { items: customers } = useAppSelector((state) => state.customer);
  const { items: paymentMethods } = useAppSelector(
    (state) => state.paymentMethod,
  );
  const { items: currencies } = useAppSelector((state) => state.currency);
  const { items: companyCharges } = useAppSelector(
    (state) => state.companyCharge,
  );
  const { branchSettings, userDefaults } = useAppSelector(
    (state) => state.settings,
  );

  const [stkModalOpen, setStkModalOpen] = useState<boolean>(false);
  const [allocateModalOpen, setAllocateModalOpen] = useState<boolean>(false);
  const [activePaymentIndex, setActivePaymentIndex] = useState<number | null>(
    null,
  );

  // --- SAFELY MAP LISTS ---
  const safeCustomers = customers || [];
  const safePaymentMethods = paymentMethods || [];
  const safeCurrencies = currencies || [];
  const safeCharges = companyCharges || [];

  // --- DERIVED OPTIONS FOR DROPDOWNS ---
  const customerOptions = useMemo(
    () =>
      safeCustomers.map((c) => ({
        value: c.id,
        label: `${c.name} (${c.primaryContact || "No Contact"})`,
      })),
    [safeCustomers],
  );
  const currencyOptions = useMemo(
    () =>
      safeCurrencies.map((c) => ({
        value: c.code,
        label: `${c.code} - ${c.name}`,
      })),
    [safeCurrencies],
  );
  const paymentMethodOptions = useMemo(
    () => safePaymentMethods.map((pm) => ({ value: pm.id, label: pm.name })),
    [safePaymentMethods],
  );

  const validationSchema = Yup.object().shape({
    receiptType: Yup.string().required(),
    customerId: Yup.string().when("receiptType", {
      is: "CREDIT",
      then: (s) => s.required("Customer is required for credit sales."),
      otherwise: (s) => s.nullable(),
    }),
    currencyCode: Yup.string().required("Currency is required"),
    exchangeRate: Yup.number().required("Exchange rate is required").min(0.01),
    payments: Yup.array().of(
      Yup.object().shape(
        {
          // 1. paymentMethodId is required ONLY IF amount is provided
          paymentMethodId: Yup.string().when("amount", {
            is: (val: number) => val !== undefined && val !== null && val !== 0,
            then: (s) => s.required("Required"),
            otherwise: (s) => s.nullable(),
          }),
          // 2. amount is required ONLY IF paymentMethodId is provided
          amount: Yup.number()
            .transform((v, o) => (o === "" ? null : v))
            .nullable()
            .when("paymentMethodId", {
              is: (val: string) => !!val,
              then: (s) => s.required("Required").min(0.01),
              otherwise: (s) => s.nullable(),
            }),
          unallocatedPaymentId: Yup.string().nullable(),
          referenceCode: Yup.string().nullable(),
        },
        // FIX: The dependency array below resolves the cyclic error
        [["amount", "paymentMethodId"]],
      ),
    ),
  });

  // Prefill resolution: Local User Defaults > Branch Settings Defaults
  const defaultPaymentMethodId =
    userDefaults?.paymentMode || branchSettings?.defaultPaymentMethodId || "";

  const initialValues: PreviewFormValues = {
    receiptType: "CASH",
    customerId: branchSettings?.defaultCustomerId || "",
    currencyCode: branchSettings?.defaultCurrencyCode || "KES",
    exchangeRate: 1.0,
    chargeIds: [],
    payments: defaultPaymentMethodId
      ? [
          {
            paymentMethodId: defaultPaymentMethodId as string,
            amount: subtotal,
            unallocatedPaymentId: null,
            referenceCode: "",
          },
        ]
      : [
          {
            paymentMethodId: "",
            amount: "",
            unallocatedPaymentId: null,
            referenceCode: "",
          },
        ],
  };

  return (
    <AppModal
      open={open}
      onClose={loadingCreateReceipt ? () => {} : onClose}
      title="Receipt Preview & Checkout"
      width="md"
    >
      <Box sx={{ width: "100%" }}>
        <FormAlert
          message={errorCreateReceipt}
          severity="error"
          closable
          onClose={() => dispatch(receiptActions.setErrorCreate(null))}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          // Custom validation for excess non-cash payments
          validate={(values) => {
            const errors: any = {};
            const baseCurrency = branchSettings?.defaultCurrencyCode || "KES";

            // Recalculate subtotal
            const { exclusiveChargesAmount } = values.chargeIds.reduce(
              (acc, id) => {
                const charge = safeCharges.find((c) => c.id === id);
                if (charge && charge.chargeType !== "INCLUSIVE") {
                  acc.exclusiveChargesAmount +=
                    subtotal * (Number(charge.rate || 0) / 100);
                }
                return acc;
              },
              { exclusiveChargesAmount: 0 },
            );

            const baseTotalDue = subtotal + exclusiveChargesAmount;
            const exchangeRate = Number(values.exchangeRate) || 1.0;
            const displayTotalDue =
              values.currencyCode !== baseCurrency && exchangeRate > 0
                ? baseTotalDue / exchangeRate
                : baseTotalDue;

            // Sum up cash vs non-cash payments
            let totalCash = 0;
            let totalNonCash = 0;

            values.payments.forEach((p) => {
              const amt = Number(p.amount) || 0;
              if (amt > 0 && p.paymentMethodId) {
                const method = safePaymentMethods.find(
                  (m) => m.id === p.paymentMethodId,
                );
                if (method?.type === PaymentMethodType.CASH) {
                  totalCash += amt;
                } else {
                  totalNonCash += amt;
                }
              }
            });

            const totalPaid = totalCash + totalNonCash;
            const change = totalPaid - displayTotalDue;

            // Rule: Change can only be provided if enough cash was tendered to cover the excess
            if (change > 0 && totalCash < change) {
              errors.payments =
                "Excess payments are only allowed for Cash methods. Non-cash payments cannot exceed the balance due.";
            }

            return errors;
          }}
          onSubmit={(values) => {
            const exchangeRate = Number(values.exchangeRate) || 1.0;

            const payload = {
              shiftId: currentShift?.id,
              customerId: values.customerId || undefined,
              saleType: saleType,
              receiptType: values.receiptType, // From Formik state
              receiptDateTime: new Date().toISOString().split(".")[0] + "Z",
              currencyCode: values.currencyCode,
              exchangeRate: exchangeRate,
              items: items.map((i) => {
                const docPrice = Number(i.price) / exchangeRate;
                const docDiscount = Number(i.discount || 0) / exchangeRate;

                return {
                  itemId: i.itemId,
                  qty: Number(i.qty),
                  price: Number(docPrice.toFixed(4)),
                  discount: Number(docDiscount.toFixed(4)),
                };
              }),
              // Only send rows where an amount was actually entered
              payments: values.payments
                .filter((p) => Number(p.amount) > 0)
                .map((p) => ({
                  paymentMethodId: p.paymentMethodId,
                  amount: Number(p.amount),
                  referenceCode: p.referenceCode || undefined,
                  unallocatedPaymentId: p.unallocatedPaymentId || undefined,
                })),
              chargeIds: values.chargeIds,
            };
            dispatch(createReceipt(payload as unknown as ReceiptRequestDto));
          }}
        >
          {({
            values,
            setFieldValue,
            errors,
          }: FormikProps<PreviewFormValues>) => {
            // --- 1. CALCULATE CHARGES IN BASE CURRENCY ---
            const { inclusiveChargesAmount, exclusiveChargesAmount } =
              values.chargeIds.reduce(
                (acc, id) => {
                  const charge = safeCharges.find((c) => c.id === id);
                  if (charge) {
                    const chargeAmount =
                      subtotal * (Number(charge.rate || 0) / 100);
                    if (charge.chargeType === "INCLUSIVE") {
                      acc.inclusiveChargesAmount += chargeAmount;
                    } else {
                      acc.exclusiveChargesAmount += chargeAmount;
                    }
                  }
                  return acc;
                },
                { inclusiveChargesAmount: 0, exclusiveChargesAmount: 0 },
              );

            // --- 2. DETERMINE TOTALS IN BASE CURRENCY ---
            const baseTotalDue = subtotal + exclusiveChargesAmount;
            const baseCurrency = branchSettings?.defaultCurrencyCode || "KES";

            // --- 3. APPLY EXCHANGE RATE IF A DIFFERENT CURRENCY IS SELECTED ---
            let displayTotalDue = baseTotalDue;
            let displayCurrency = baseCurrency;
            const exchangeRate = Number(values.exchangeRate) || 1.0;

            if (values.currencyCode !== baseCurrency && exchangeRate > 0) {
              displayTotalDue = baseTotalDue / exchangeRate;
              displayCurrency = values.currencyCode;
            }

            // --- 4. CALCULATE PAYMENTS AND BALANCE IN THE FINAL DISPLAY CURRENCY ---
            const totalPaid = values.payments.reduce(
              (sum, p) => sum + (Number(p.amount) || 0),
              0,
            );
            const balance = totalPaid - displayTotalDue;

            const isCashValid = totalPaid >= displayTotalDue;
            const isCreditValid =
              values.customerId && totalPaid < displayTotalDue;

            const getCustomerPhone = (): string => {
              if (!values.customerId) return "";
              const c = safeCustomers.find((c) => c.id === values.customerId);
              return c ? c.primaryContact : "";
            };

            return (
              <Form>
                <Stack spacing={3}>
                  <Typography variant="overline" color="text.secondary">
                    Receipt Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormSearchSelect
                        name="customerId"
                        label="Select Customer (Req. for Credit)"
                        options={customerOptions}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Apply Charges (Optional)</InputLabel>
                        <Select
                          multiple
                          name="chargeIds"
                          value={values.chargeIds}
                          onChange={(e) =>
                            setFieldValue("chargeIds", e.target.value)
                          }
                          input={
                            <OutlinedInput label="Apply Charges (Optional)" />
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {(selected as string[]).map((value) => {
                                const charge = safeCharges.find(
                                  (c) => c.id === value,
                                );
                                return (
                                  <Chip
                                    key={value}
                                    label={`${charge?.name} (${charge?.chargeType === "INCLUSIVE" ? "Incl." : "Excl."})`}
                                    size="small"
                                    onDelete={() => {
                                      const newCharges =
                                        values.chargeIds.filter(
                                          (id) => id !== value,
                                        );
                                      setFieldValue("chargeIds", newCharges);
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {safeCharges.map((charge) => (
                            <MenuItem key={charge.id} value={charge.id}>
                              {charge.name} ({charge.rate}%) -{" "}
                              {charge.chargeType}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormSearchSelect
                        name="currencyCode"
                        label="Currency"
                        options={currencyOptions}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormTextField
                        name="exchangeRate"
                        label="Exchange Rate"
                        type="number"
                      />
                    </Grid>
                  </Grid>

                  <Divider />
                  <Typography variant="overline" color="text.secondary">
                    Payments Received
                  </Typography>

                  {/* Surface custom validation error here */}
                  {errors.payments && typeof errors.payments === "string" && (
                    <Alert severity="error">{errors.payments}</Alert>
                  )}

                  <FieldArray name="payments">
                    {({ push, remove }) => (
                      <Stack spacing={2}>
                        {values.payments.map((p, index) => (
                          <Box
                            key={index}
                            sx={{
                              border: "1px solid #e0e0e0",
                              p: 1.5,
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={2}
                              sx={{ alignItems: "center" }}
                            >
                              <Box sx={{ flex: 2 }}>
                                <FormSelectField
                                  name={`payments.${index}.paymentMethodId`}
                                  label="Payment Method"
                                  options={paymentMethodOptions}
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <FormTextField
                                  name={`payments.${index}.amount`}
                                  label="Amount"
                                  type="number"
                                  disabled={!!p.unallocatedPaymentId}
                                />
                              </Box>
                              <IconButton
                                onClick={() => remove(index)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>

                            {/* Electronic Payment Integration Logic */}
                            {branchSettings?.paymentIntegration?.active && (
                              <Stack
                                direction="row"
                                spacing={2}
                                sx={{ mt: 1.5, alignItems: "center" }}
                              >
                                {p.unallocatedPaymentId ? (
                                  <Chip
                                    color="success"
                                    icon={<CheckCircleIcon />}
                                    label={`Linked Ref: ${p.referenceCode}`}
                                    onDelete={() => {
                                      setFieldValue(
                                        `payments.${index}.unallocatedPaymentId`,
                                        null,
                                      );
                                      setFieldValue(
                                        `payments.${index}.referenceCode`,
                                        "",
                                      );
                                    }}
                                  />
                                ) : (
                                  <>
                                    {branchSettings.paymentIntegration
                                      .allowsMpesaStk && (
                                      <AppButton
                                        pill
                                        size="small"
                                        startIcon={<MpesaIcon />}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                          setActivePaymentIndex(index);
                                          setStkModalOpen(true);
                                        }}
                                      >
                                        Send STK Push
                                      </AppButton>
                                    )}
                                    <AppButton
                                      pill
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      startIcon={<LinkIcon />}
                                      onClick={() => {
                                        setActivePaymentIndex(index);
                                        setAllocateModalOpen(true);
                                      }}
                                    >
                                      Link Received Payment
                                    </AppButton>
                                  </>
                                )}
                              </Stack>
                            )}
                          </Box>
                        ))}
                        <AppButton
                          variant="text"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            push({
                              paymentMethodId: "",
                              amount: "",
                              unallocatedPaymentId: null,
                              referenceCode: "",
                            })
                          }
                        >
                          Add Payment Split
                        </AppButton>
                      </Stack>
                    )}
                  </FieldArray>

                  <Divider />

                  {/* --- TOTALS PANE --- */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{ justifyContent: "space-between", mb: 1 }}
                    >
                      <Typography>Cart Subtotal ({baseCurrency}):</Typography>
                      <Typography>
                        {baseCurrency} {subtotal.toFixed(2)}
                      </Typography>
                    </Stack>

                    {exclusiveChargesAmount > 0 && (
                      <Stack
                        direction="row"
                        sx={{ justifyContent: "space-between", mb: 1 }}
                      >
                        <Typography color="text.secondary">
                          Exclusive Charges ({baseCurrency}):
                        </Typography>
                        <Typography color="text.secondary">
                          {baseCurrency} {exclusiveChargesAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                    )}

                    {inclusiveChargesAmount > 0 && (
                      <Stack
                        direction="row"
                        sx={{ justifyContent: "space-between", mb: 1 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          ↳ Includes internal charges of {baseCurrency}{" "}
                          {inclusiveChargesAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Stack
                      direction="row"
                      sx={{ justifyContent: "space-between", mb: 1 }}
                    >
                      <Typography variant="h6">Amount Payable:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {displayCurrency} {displayTotalDue.toFixed(2)}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      sx={{ justifyContent: "space-between", mb: 1 }}
                    >
                      <Typography>Total Paid:</Typography>
                      <Typography
                        color="success.main"
                        sx={{ fontWeight: "bold" }}
                      >
                        {displayCurrency} {totalPaid.toFixed(2)}
                      </Typography>
                    </Stack>

                    {balance > 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <strong>Change to return: </strong> {displayCurrency}{" "}
                        {balance.toFixed(2)}
                      </Alert>
                    )}
                  </Box>

                  <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                    <AppButton
                      fullWidth
                      pill
                      variant="contained"
                      color="primary"
                      disabled={!isCashValid}
                      // Use setFieldValue to sync intent before validation/submission
                      onClick={() => setFieldValue("receiptType", "CASH")}
                      type="submit"
                      isLoading={
                        loadingCreateReceipt && values.receiptType === "CASH"
                      }
                      loadingText="Processing..."
                    >
                      Complete Cash Sale
                    </AppButton>
                    <AppButton
                      fullWidth
                      pill
                      variant="contained"
                      color="warning"
                      disabled={!isCreditValid}
                      onClick={() => setFieldValue("receiptType", "CREDIT")}
                      type="submit"
                      isLoading={
                        loadingCreateReceipt && values.receiptType === "CREDIT"
                      }
                      loadingText="Processing..."
                    >
                      Complete Credit Sale
                    </AppButton>
                  </Stack>
                </Stack>

                {stkModalOpen && (
                  <StkPushModal
                    open={stkModalOpen}
                    onClose={() => setStkModalOpen(false)}
                    defaultAmount={
                      values.payments[activePaymentIndex as number]
                        ?.amount as number
                    }
                    defaultPhone={getCustomerPhone()}
                  />
                )}

                {allocateModalOpen && (
                  <AllocatePaymentModal
                    open={allocateModalOpen}
                    onClose={() => setAllocateModalOpen(false)}
                    onSelect={(payment) => {
                      setFieldValue(
                        `payments.${activePaymentIndex}.unallocatedPaymentId`,
                        payment.id,
                      );
                      setFieldValue(
                        `payments.${activePaymentIndex}.referenceCode`,
                        payment.gatewayReceiptNumber,
                      );
                      setFieldValue(
                        `payments.${activePaymentIndex}.amount`,
                        payment.amount,
                      );
                      setAllocateModalOpen(false);
                    }}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </Box>
    </AppModal>
  );
};

export default ReceiptPreviewModal;
